import './index.scss';

import dayjs from 'dayjs';
import { InputField } from 'fields/base/InputField/InputField';
import { DateField } from 'fields/DateField';
import { DurationField } from 'fields/DurationField';
import { motion } from 'framer-motion';
import capitalize from 'lodash/capitalize';
import isNil from 'lodash/isNil';
import { memo, useEffect, useMemo, useState } from 'react';
import { useRendersCount } from 'react-use';

import { useConvertedCurrencies, useTaskTime } from 'hooks/dex';
import { useFeeCostsUiString } from 'hooks/dex/useFeeCostsUiString';
import { ConnectWalletsToDexBuySellTaskModal } from 'modals/ConnectWalletsToDexBuySellTaskModal';
import { useTypedSelector } from 'store';
import {
  EnumDexBuySellOpts,
  EnumDexBuySellType,
  IBuySellBotTaskItemInner,
  IDexBuySellBotDirection,
  IDexBuySellBotTypeUnit,
} from 'types/bots';
import { Button } from 'ui/Button/Button';
import { ButtonLoading } from 'ui/Button/ButtonLoading';
import { ErrorText } from 'ui/ErrorText/ErrorText';
import { CreateTaskForm } from 'ui/forms';
import { Modal } from 'ui/Modal/Modal';
import { Tabs } from 'ui/Tabs';
import { ToggleRadio } from 'ui/ToggleRadio/ToggleRadio';
import { mapDexBuySellTaskType } from 'utils/dex/mapDexBuySellTaskType';

import { Currencies } from './Currencies';
import { useTaskModal } from './useTaskModal/useTaskModal';

type Direction = 'Sell' | 'Buy';

type DexBuySellTaskModalProps = {
  mode: 'create' | 'edit';
  task?: IBuySellBotTaskItemInner;
  onClose: VoidFunction;
  onOpen: VoidFunction;
};

export const DexBuySellTaskModal = memo(({ mode, task, onClose }: DexBuySellTaskModalProps) => {
  const dexPair = useTypedSelector(store => store.pairs.selectedDexPair)!;

  const [direction, setDirection] = useState<Capitalize<IDexBuySellBotDirection>>('Sell');
  const [currentTabKey, setCurrentTabKey] = useState<IDexBuySellBotTypeUnit>(
    EnumDexBuySellType.TYPE_1W_NS,
  );
  const [isOpenWalletsModal, setIsOpenWalletsModal] = useState(false);

  const rendersCount = useRendersCount();

  const {
    loading,
    values,
    handleSubmit,
    setFieldValue,
    formError,
    totalCurrency,
    setTotalCurrency,
    tradesCurrency,
    setTradesCurrency,
    thresholdCurrency,
    setThresholdCurrency,
    resetForm,
    edited,
    errors,
    touched,
  } = useTaskModal({
    direction,
    onClose,
    task,
    mode,
  });

  const isTaskRunning = useMemo(
    () => task?.status === 'current' || task?.status === 'past',
    [task],
  );

  const isType1wNs = useMemo(
    () => currentTabKey === EnumDexBuySellType.TYPE_1W_NS,
    [currentTabKey],
  );

  const convertedAmount = useConvertedCurrencies({
    amount: values.totalTokenAmount,
    selectedCurrency: totalCurrency,
  });
  const convertedMinAmount = useConvertedCurrencies({
    amount: values.minAmount,
    selectedCurrency: tradesCurrency,
  });
  const convertedMaxAmount = useConvertedCurrencies({
    amount: values.maxAmount,
    selectedCurrency: tradesCurrency,
  });
  const feeCostsUiString = useFeeCostsUiString({
    totalTokenAmount: values.totalTokenAmount,
    minAmount: values.minAmount,
    maxAmount: values.maxAmount,
  });

  useEffect(() => {
    if (task) {
      const taskOptsKey = mapDexBuySellTaskType(task.task.type);

      setCurrentTabKey(task.task.type);

      if (taskOptsKey === EnumDexBuySellOpts.OPT_TYPE_1W_NS) {
        const directionValue = capitalize(
          task.task[EnumDexBuySellOpts.OPT_TYPE_1W_NS].direction,
        ) as Direction;

        setDirection(directionValue);
      }

      if (taskOptsKey === EnumDexBuySellOpts.OPT_TYPE_1W_1S) {
        const directionValue = capitalize(
          task.task[EnumDexBuySellOpts.OPT_TYPE_1W_1S].direction,
        ) as Direction;

        setDirection(directionValue);
      }
    }
  }, [task]);

  const { finishTimeUiString } = useTaskTime({
    minAmount: values.minAmount,
    maxAmount: values.maxAmount,
    minDelay: values.minDelay ?? undefined,
    maxDelay: values.maxDelay ?? undefined,
    totalTokenAmount: values.totalTokenAmount,
  });

  const handleChangeTabKey = (tabIndex: number) => {
    const typeOptsKey =
      tabIndex === 0 ? EnumDexBuySellType.TYPE_1W_NS : EnumDexBuySellType.TYPE_1W_1S;

    setCurrentTabKey(typeOptsKey);

    resetForm();
    setFieldValue('type', typeOptsKey);
  };

  const priceThresholdCurrencies = [
    `${dexPair.token_base.symbol} / ${dexPair.token_quote.symbol}`,
    `${dexPair.token_base.symbol} / USD`,
  ];

  return (
    <>
      <Modal
        title={`${capitalize(mode)} task`}
        edited={edited}
        className="mm-dex-buy-sell-bot-task-modal"
        onClose={onClose}
      >
        {mode === 'create' && (
          <Tabs
            tabs={[
              { label: '1 wallet - n swaps', content: null },
              { label: '1 wallet - 1 swap', content: null },
            ]}
            onChange={handleChangeTabKey}
            className="pt-0"
          />
        )}

        <CreateTaskForm>
          <div className="form-inner scrollable">
            <div className="mm-add-pair-modal">
              <ToggleRadio
                disabled={isTaskRunning}
                label="Direction"
                titles={['Sell', 'Buy']}
                name="new-task-direction"
                value={direction}
                onChange={e => setDirection(e.target.value as Direction)}
              />

              <DateField
                date={values.startDate ? dayjs(values.startDate).valueOf() : null}
                label="Start time"
                setDate={value =>
                  setFieldValue(
                    'startDate',
                    !isNil(value) ? dayjs(value).second(0).millisecond(0).valueOf() : null,
                  )
                }
                readonly={isTaskRunning}
                errorMessage={errors.startDate && touched.startDate ? errors.startDate : undefined}
              />

              {mode === 'edit' && (
                <InputField
                  type="decimal-number"
                  label="Wallets"
                  value={
                    task?.task.wallets_total === 0
                      ? 'All wallets'
                      : `${task?.task.wallets_total} wallets`
                  }
                  readonly
                  nodeRight={
                    <button
                      onClick={() => setIsOpenWalletsModal(true)}
                      className="wallets-count-text"
                    >
                      Choose wallets
                    </button>
                  }
                />
              )}

              {isType1wNs && (
                <div>
                  <div className="header-currencies">
                    <span className="currency-title">Total amount in</span>
                    <Currencies selected={totalCurrency} onSelect={setTotalCurrency} />
                  </div>
                  <InputField
                    type="decimal-number"
                    value={values.totalTokenAmount}
                    label="Amount"
                    decimals={dexPair.token_base.decimals}
                    setValue={v => setFieldValue('totalTokenAmount', v ? v : '')}
                    errorMessage={
                      errors.totalTokenAmount && touched.totalTokenAmount
                        ? errors.totalTokenAmount
                        : undefined
                    }
                    nodeRight={<span className="input-right-caption">{convertedAmount}</span>}
                  />
                </div>
              )}

              <div>
                <div className="header-currencies">
                  <span className="currency-title">Price threshold</span>
                  <Currencies
                    currencies={priceThresholdCurrencies}
                    selected={
                      thresholdCurrency === 'usd'
                        ? priceThresholdCurrencies[1]
                        : priceThresholdCurrencies[0]
                    }
                    onSelect={v =>
                      setThresholdCurrency(v === priceThresholdCurrencies[1] ? 'usd' : 'quote')
                    }
                  />
                </div>

                <InputField
                  type="decimal-number"
                  value={values.priceThresholdMin}
                  label="Min threshold"
                  setValue={v => {
                    setFieldValue('priceThresholdMin', v ? v : '');
                    setTimeout(() => {
                      setFieldValue('priceThresholdMax', v ? v : '');
                    }, 0);
                  }}
                  decimals={dexPair.token_quote.decimals}
                  errorMessage={
                    errors.priceThresholdMin && touched.priceThresholdMin
                      ? errors.priceThresholdMin
                      : undefined
                  }
                />
                <InputField
                  type="decimal-number"
                  value={values.priceThresholdMax}
                  label="Max threshold"
                  setValue={v => setFieldValue('priceThresholdMax', v ? v : '')}
                  decimals={dexPair.token_quote.decimals}
                  errorMessage={
                    errors.priceThresholdMax && touched.priceThresholdMax
                      ? errors.priceThresholdMax
                      : undefined
                  }
                />
              </div>

              <div>
                <div className="header-currencies">
                  <span className="currency-title"> One trade amount in</span>
                  <Currencies selected={tradesCurrency} onSelect={setTradesCurrency} />
                </div>

                <InputField
                  type="decimal-number"
                  value={values.minAmount}
                  decimals={dexPair.token_base.decimals}
                  label="Minimum"
                  setValue={v => setFieldValue('minAmount', v ? v : '')}
                  errorMessage={
                    errors.minAmount && touched.minAmount ? errors.minAmount : undefined
                  }
                  nodeRight={<span className="input-right-caption">{convertedMinAmount}</span>}
                />
                <InputField
                  type="decimal-number"
                  value={values.maxAmount}
                  decimals={dexPair.token_base.decimals}
                  label="Maximum"
                  setValue={v => setFieldValue('maxAmount', v ? v : '')}
                  errorMessage={
                    errors.maxAmount && touched.maxAmount ? errors.maxAmount : undefined
                  }
                  nodeRight={<span className="input-right-caption">{convertedMaxAmount}</span>}
                />
              </div>

              <motion.div
                initial={rendersCount === 1 ? false : { height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.6, ease: 'easeInOut' }}
                key="trades-pause-params"
                className="trades-pause-container"
              >
                <div className="text-primary font-bold mb-1.5 text-sm">Pause between trades</div>
                <div className="inputs-row">
                  <DurationField
                    value={values.minDelay}
                    setValue={v => {
                      const newValue = isNil(v) ? null : v;

                      setFieldValue('minDelay', newValue);
                    }}
                    label="Min delay"
                    id="min-delay"
                    zeroable
                    errorMessage={errors.minDelay && touched.minDelay ? errors.minDelay : undefined}
                  />
                  <DurationField
                    value={values.maxDelay}
                    setValue={v => {
                      const newValue = isNil(v) ? null : v;

                      setFieldValue('maxDelay', newValue);
                    }}
                    label="Max delay"
                    id="max-delay"
                    zeroable
                    errorMessage={errors.maxDelay && touched.maxDelay ? errors.maxDelay : undefined}
                  />
                </div>
              </motion.div>

              <div className="grid grid-flow-row gap-2.5 text-primary font-normal">
                <div className="grid grid-flow-col">
                  <span className="text-sm">Finish time:</span>
                  <span className="text-right text-sm">
                    {finishTimeUiString ? finishTimeUiString : 'Not defined'}
                  </span>
                </div>

                <div className="grid grid-flow-col">
                  <span className="text-sm">Fee costs:</span>
                  <span className="text-right text-sm">
                    {feeCostsUiString ? feeCostsUiString : 'Not defined'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="mm-new-buy-sell-bot-task-actions">
            {formError && <ErrorText>{formError}</ErrorText>}
            {loading ? (
              <ButtonLoading />
            ) : (
              <Button onClick={() => handleSubmit()}>
                {mode === 'create' ? 'Create task' : 'Save'}
              </Button>
            )}
          </div>
        </CreateTaskForm>
      </Modal>

      {isOpenWalletsModal && task && (
        <ConnectWalletsToDexBuySellTaskModal
          task={task?.task}
          onClose={() => {
            setIsOpenWalletsModal(false);
            onClose();
          }}
        />
      )}
    </>
  );
});
